/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 */

import {inject, Pipe, PipeTransform} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';

import {ID_SHORT_TRANSLATION_MAP} from './id-short-translation-map';

/**
 * Translates the given key. Falls back to the provided fallback string
 * if the key is not found in the translation files.
 */
@Pipe({name: 'translateOrFallback', pure: false})
export class TranslateOrFallbackPipe implements PipeTransform {
  private readonly translate = inject(TranslateService);

  transform(key: string | undefined | null, fallback: string): string {
    const normalizedKey = key?.trim();

    if (!normalizedKey) {
      return this.splitCamelCase(fallback);
    }

    // idShort values are domain identifiers, not guaranteed translation keys.
    // We normalize them to i18n keys first so templates can stay generic.
    const translationKey =
      ID_SHORT_TRANSLATION_MAP[normalizedKey] ?? normalizedKey;
    const result = this.translate.instant(translationKey);

    if (result === translationKey) {
      return this.splitCamelCase(fallback);
    }

    return result;
  }

  // Keeps a readable fallback label when no translation key is available.
  private splitCamelCase(value: string): string {
    return value
      .replaceAll(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replaceAll(/([A-Z])([A-Z][a-z])/g, '$1 $2');
  }
}
